import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useMemo, useRef, useState } from "react";
import { Waypoints, Upload, X, Loader2, Download, Trash2, FileText, AlertTriangle, History, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { downloadFromUrl } from "@/lib/download";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { validateTraceName, validateTraceText } from "@/lib/sipTraceGuard";
import { useAuth } from "@/context/AuthContext";
import { SipLadder } from "@/components/SipLadder";
import { QueueInsight } from "@/components/QueueInsight";
import { QueueRollup } from "@/components/QueueRollup";

const MAX_FILES = 5;
const MAX_BYTES = 5 * 1024 * 1024;
const SAMPLE_TRACE = `timestamp,event,direction,from,to,sip_code,leg,detail
2026-04-21 08:31:52.104,INVITE,inbound,+14035550142,+15875550100,,UC SBC,Inbound call from PSTN carrier
2026-04-21 08:31:52.210,100 Trying,inbound,+14035550142,+15875550100,100,UC SBC,Carrier trunk trying
2026-04-21 08:31:52.480,INVITE,internal,+14035550142,queue-support,,UC PBX,Routed to Support auto-attendant
2026-04-21 08:31:53.005,180 Ringing,internal,+14035550142,queue-support,180,Queues,Entered Support queue (position 1)
2026-04-21 08:31:53.100,QUEUE,internal,+14035550142,queue-support,,Queues,Ring-all to 3 agents
2026-04-21 08:32:41.320,180 Ringing,internal,+14035550142,ext-201,180,Endpoints,Agent ext 201 ringing
2026-04-21 08:32:58.660,200 OK,internal,+14035550142,ext-201,200,Endpoints,Agent ext 201 answered
2026-04-21 08:32:58.700,ACK,internal,+14035550142,ext-201,,UC PBX,Media established (talk)
2026-04-21 08:37:15.900,BYE,internal,ext-201,+14035550142,,Endpoints,Agent hung up
2026-04-21 08:37:16.010,200 OK,inbound,+15875550100,+14035550142,200,UC SBC,Normal clearing (Q.850 cause 16)`;
const SAMPLE_FILE = { name: "sample-call-trace.csv", content: SAMPLE_TRACE };
const PILL = {
  invite: { bg: "#E6F1FB", fg: "#0C447C" }, trying: { bg: "#F1EFE8", fg: "#444441" },
  ring: { bg: "#FAEEDA", fg: "#633806" }, ok: { bg: "#EAF3DE", fg: "#27500A" },
  cancel: { bg: "#FCEBEB", fg: "#791F1F" }, bye: { bg: "#FAECE7", fg: "#712B13" },
  queue: { bg: "#EEEDFE", fg: "#3C3489" }, hg: { bg: "#E1F5EE", fg: "#085041" },
  talk: { bg: "#EAF3DE", fg: "#173404" }, moh: { bg: "#FBEAF0", fg: "#72243E" },
  default: { bg: "#F1F5F9", fg: "#334155" },
};
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function SummaryGrid({ items }) {
  if (!items?.length) return null;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="sip-summary">
      {items.map((it, i) => (
        <div key={i} className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">{it.label}</div>
          <div className="mt-0.5 text-sm font-semibold text-slate-800 break-words">{it.value || "—"}</div>
          {it.sub && <div className="text-[11px] text-slate-500">{it.sub}</div>}
        </div>
      ))}
    </div>
  );
}

function Timeline({ events }) {
  if (!events?.length) return null;
  let curPhase = null;
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200" data-testid="sip-timeline">
      <div className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[11px] text-slate-400">Columns: UC SBC | UC PBX | Queues | Endpoints</div>
      <div className="divide-y divide-slate-50">
        {events.map((ev, i) => {
          const showPhase = ev.phase && ev.phase !== curPhase;
          if (showPhase) curPhase = ev.phase;
          const tone = PILL[(ev.type || "default").toLowerCase()] || PILL.default;
          return (
            <div key={i}>
              {showPhase && <div className="bg-slate-100 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{ev.phase}</div>}
              <div className="flex items-start gap-3 px-3 py-1.5 hover:bg-slate-50/60">
                <span className="w-16 shrink-0 pt-0.5 font-mono text-[11px] text-slate-500">{ev.time}</span>
                <span className="w-16 shrink-0 pt-0.5 text-[11px] text-slate-400">{ev.column}</span>
                <span className="shrink-0 rounded px-2 py-0.5 text-[11px] font-semibold" style={{ background: tone.bg, color: tone.fg }}>{ev.label}</span>
                <span className="flex-1 text-xs text-slate-600">{ev.detail}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DataTable({ headers, rows, testid }) {
  if (!headers?.length || !rows?.length) return null;
  return (
    <div className="overflow-auto rounded-lg border border-slate-200" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-[#33456b] text-white"><tr>{headers.map((h, i) => <th key={i} className="px-3 py-2 text-left text-xs font-semibold">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className={i % 2 ? "bg-slate-50" : "bg-white"}>{r.map((c, j) => <td key={j} className="px-3 py-1.5 text-xs text-slate-600 align-top">{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function FileAnalysis({ file }) {
  const a = file.analysis || {};
  const diag = a.diagnosis || {};
  const ctx = a.context || {};
  return (
    <div className="space-y-5">
      {a.error && <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"><AlertTriangle className="h-4 w-4" /> {a.error}</div>}
      {file.truncated && <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">This file was very large — the middle was trimmed so the full SIP setup and teardown fit. Setup (first 60%) and hangup/teardown (last 40%) were analysed in full.</div>}
      {(ctx.client || ctx.date || ctx.call_id) && <div className="text-xs text-slate-500">{[ctx.client, ctx.date, ctx.call_id, ctx.source].filter(Boolean).join(" · ")}</div>}
      {ctx.escalation && <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800"><span className="font-semibold">Client escalation:</span> {ctx.escalation}</div>}
      <SummaryGrid items={a.summary} />
      {a.evidence?.headers?.length > 0 && (
        <div className="space-y-1.5">
          <h3 className="text-sm font-semibold text-slate-800">{a.evidence.title || "Evidence"}</h3>
          {a.evidence.intro && <p className="text-xs text-slate-500">{a.evidence.intro}</p>}
          <DataTable headers={a.evidence.headers} rows={a.evidence.rows} testid="sip-evidence" />
        </div>
      )}
      {a.timeline?.length > 0 && <div className="space-y-1.5"><h3 className="text-sm font-semibold text-slate-800">Call Flow Timeline</h3><Timeline events={a.timeline} /></div>}
      {a.ladder?.hops?.length > 0 && <div className="space-y-1.5"><h3 className="text-sm font-semibold text-slate-800">Call Flow Ladder</h3><SipLadder ladder={a.ladder} title={file.name} /></div>}
      {(diag.what_happened || diag.issues?.length || diag.recommendations?.length) && (
        <div className="space-y-2 rounded-lg border border-slate-200 p-4" data-testid="sip-diagnosis">
          <h3 className="text-sm font-semibold text-slate-800">Diagnosis</h3>
          {diag.what_happened && <div><div className="text-xs font-semibold text-slate-600">What happened</div><p className="text-sm text-slate-600">{diag.what_happened}</p></div>}
          {diag.root_cause && <div><div className="text-xs font-semibold text-slate-600">Root cause</div><p className="text-sm text-slate-600">{diag.root_cause}</p></div>}
          {diag.issues?.length > 0 && <div><div className="text-xs font-semibold text-rose-600">Issues detected</div><ul className="list-disc pl-5 text-sm text-slate-600">{diag.issues.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
          {diag.recommendations?.length > 0 && <div><div className="text-xs font-semibold text-emerald-700">Recommendations</div><ul className="list-disc pl-5 text-sm text-slate-600">{diag.recommendations.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
        </div>
      )}
      {a.technical_details?.length > 0 && (
        <div className="space-y-1.5"><h3 className="text-sm font-semibold text-slate-800">Technical Details</h3>
          <DataTable headers={["Parameter", "Value"]} rows={a.technical_details.map((t) => [t.parameter, t.value])} testid="sip-tech" /></div>
      )}
    </div>
  );
}

export default function SipTraceAnalysis() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [files, setFiles] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [traceUrl, setTraceUrl] = useState("");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("0");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [authorFilter, setAuthorFilter] = usePersistedState("sip-traces.author", "all");
  const [orgFilter, setOrgFilter] = usePersistedState("sip-traces.org", "all");
  const inputRef = useRef(null);

  const loadHistory = () => api.get("/sip-trace/analyses").then((r) => setHistory(r.data || [])).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const addFiles = async (fileList) => {
    const incoming = Array.from(fileList || []);
    let next = [...files];
    for (const f of incoming) {
      const nameErr = validateTraceName(f.name);
      if (nameErr) { toast.error(nameErr); continue; }
      if (f.size > MAX_BYTES) { toast.error(`${f.name}: exceeds 5 MB`); continue; }
      if (next.length >= MAX_FILES) { toast.error(`Up to ${MAX_FILES} files at a time`); break; }
      if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
      // Iter 115g — content is screened before it can be submitted: plain-text
      // CSV, expected call-trace header, no embedded code/formulas.
      let text = "";
      try { text = await readText(f); } catch { toast.error(`${f.name}: could not be read`); continue; }
      const err = validateTraceText(f.name, text);
      if (err) { toast.error(err, { duration: 8000 }); continue; }
      next.push(f);
    }
    setFiles(next);
  };
  const removeFile = (i) => setFiles((f) => f.filter((_, idx) => idx !== i));

  const readText = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsText(file); });

  const analyze = async () => {
    if (!files.length) { toast.error("Add at least one CSV"); return; }
    const payloadFiles = await Promise.all(files.map(async (f) => ({ name: f.name, content: await readText(f) })));
    setFiles([]);
    await runAnalyze(payloadFiles);
  };

  const analyzeSample = () => runAnalyze([SAMPLE_FILE], "Sample call trace");

  const runAnalyze = async (payloadFiles, title) => {
    setAnalyzing(true); setResult(null);
    try {
      const { data } = await api.post("/sip-trace/analyze", { files: payloadFiles, ...(title ? { title } : {}) });
      loadHistory();
      if (data.status === "processing") { pollAnalysis(data.analysis_id); }
      else { setResult(data); setActiveTab("0"); setAnalyzing(false); toast.success("Analysis complete"); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Analysis failed"); setAnalyzing(false); }
  };

  // Iter 115w — paste a NetSapiens portal trace export link instead of a file.
  const analyzeUrl = async () => {
    if (!traceUrl.trim()) { toast.error("Paste the portal trace URL first."); return; }
    setAnalyzing(true); setResult(null);
    try {
      const { data } = await api.post("/sip-trace/analyze-url", { url: traceUrl.trim() });
      setTraceUrl("");
      loadHistory();
      if (data.status === "processing") pollAnalysis(data.analysis_id);
      else { setResult(data); setActiveTab("0"); setAnalyzing(false); }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not fetch that trace", { duration: 9000 });
      setAnalyzing(false);
    }
  };

  const pollAnalysis = (id) => {
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/sip-trace/analyses/${id}`);
        if (data.status === "done") { clearInterval(iv); setResult(data); setActiveTab("0"); setAnalyzing(false); loadHistory(); toast.success("Analysis complete"); }
        else if (data.status === "error") { clearInterval(iv); setAnalyzing(false); loadHistory(); toast.error(data.error || "Analysis failed"); }
        else if (tries > 60) { clearInterval(iv); setAnalyzing(false); loadHistory(); toast.error("Analysis is taking longer than expected — check 'Recent analyses' shortly."); }
      } catch { clearInterval(iv); setAnalyzing(false); }
    }, 3000);
  };

  const openHistory = async (id) => {
    try {
      const { data } = await api.get(`/sip-trace/analyses/${id}`);
      // Iter 91k — history rows can be in three states. Only "done"
      // has a populated `files` array; the previous version blindly
      // set the result regardless, which rendered an empty PDF-only
      // shell for stuck "processing" rows (e.g. after a backend restart
      // killed the background task mid-flight).
      if (data.status === "done") {
        setResult(data); setActiveTab("0"); setAnalyzing(false);
        return;
      }
      if (data.status === "error") {
        setResult(null); setAnalyzing(false);
        toast.error(data.error || "That analysis failed — try re-running it.");
        return;
      }
      // status === "processing" — resume polling so the UI catches up
      // when the background task finishes.
      setResult(null); setAnalyzing(true);
      toast.message("Picking up where the analysis left off…");
      pollAnalysis(id);
    } catch {
      toast.error("Could not open analysis");
    }
  };
  const deleteHistory = async (id, e) => {
    e.stopPropagation();
    try { await api.delete(`/sip-trace/analyses/${id}`); toast.success("Deleted"); if (result?.analysis_id === id) setResult(null); loadHistory(); }
    catch { toast.error("Delete failed"); }
  };
  const downloadPdf = async (idx) => {
    if (!result) return;
    setPdfBusy(true);
    const t = toast.loading("Generating PDF…");
    try { const { data } = await api.post(`/sip-trace/analyses/${result.analysis_id}/pdf`, { file_index: idx }); await downloadFromUrl(`/files/${data.token}`, data.filename); toast.success("PDF downloaded", { id: t }); }
    catch { toast.error("PDF export failed", { id: t }); }
    finally { setPdfBusy(false); }
  };
  const downloadZip = async () => {
    if (!result) return;
    setPdfBusy(true);
    const t = toast.loading("Bundling PDFs…");
    try { const { data } = await api.post(`/sip-trace/analyses/${result.analysis_id}/pdf-zip`); downloadFromUrl(`/files/${data.token}`, data.filename); toast.success("ZIP exported", { id: t }); }
    catch { toast.error("ZIP export failed", { id: t }); }
    finally { setPdfBusy(false); }
  };

  const resultFiles = result?.files || [];
  const showComparison = (result?.comparison || []).length > 1;

  const authorOptions = useMemo(
    () => Array.from(new Set(history.map((h) => h.created_by_name).filter(Boolean))).sort(),
    [history]);
  const orgOptions = useMemo(
    () => Array.from(new Set(history.map((h) => h.owner_org_name).filter(Boolean))).sort(),
    [history]);
  const visibleHistory = history.filter((h) =>
    (authorFilter === "all" || h.created_by_name === authorFilter) &&
    (orgFilter === "all" || h.owner_org_name === orgFilter));

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-1" data-testid="sip-trace-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900"><Waypoints className="h-6 w-6 text-primary" /> SIP Trace Analysis</h1>
        <p className="mt-1 text-sm text-slate-500">Upload up to 5 call-trace CSVs to get an AI call-flow timeline, summary and diagnosis — plus a downloadable PDF.</p>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_280px]">
        <div className="space-y-5">
          {/* Uploader */}
          <Card className="p-5">
            <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
              className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/60 py-8 text-center">
              <Upload className="h-7 w-7 text-slate-400" />
              <p className="mt-2 text-sm text-slate-600">Drag &amp; drop CSV files here, or</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => inputRef.current?.click()} data-testid="sip-browse-button">Browse files</Button>
              <input ref={inputRef} type="file" accept=".csv,text/csv" multiple className="hidden" data-testid="sip-upload-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
              <p className="mt-2 text-[11px] text-slate-400" data-testid="sip-upload-hint">CSV only · up to {MAX_FILES} files · 5 MB each</p>
              <button onClick={analyzeSample} disabled={analyzing} data-testid="sip-sample-button"
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline disabled:opacity-50">
                <Sparkles className="h-3.5 w-3.5" /> No file handy? Analyze a sample trace
              </button>
            </div>
            {files.length > 0 && (
              <div className="mt-3 space-y-2">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border border-slate-100 bg-white px-3 py-2" data-testid={`sip-file-chip-${i}`}>
                    <div className="flex items-center gap-2 text-sm text-slate-700"><FileText className="h-4 w-4 text-slate-400" /> {f.name} <span className="text-xs text-slate-400">({(f.size / 1024).toFixed(0)} KB)</span></div>
                    <button onClick={() => removeFile(i)} data-testid={`sip-remove-${i}`} className="text-slate-400 hover:text-rose-500"><X className="h-4 w-4" /></button>
                  </div>
                ))}
                <Button onClick={analyze} disabled={analyzing} className="w-full" data-testid="sip-analyze-button">
                  {analyzing ? <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing {files.length} file(s)…</> : <><Sparkles className="h-4 w-4" /> Analyze {files.length} file(s)</>}
                </Button>
              </div>
            )}
            {/* Iter 115w — NetSapiens portal trace link import. */}
            <div className="mt-4 border-t border-slate-100 pt-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Or paste a portal trace link</div>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <Input data-testid="sip-url-input" value={traceUrl} onChange={(e) => setTraceUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") analyzeUrl(); }}
                  placeholder="https://portal.example.ca/ns-api/?object=trace&action=export&k=…&location=…"
                  className="font-mono text-[11px]" />
                <Button onClick={analyzeUrl} disabled={analyzing || !traceUrl.trim()} data-testid="sip-url-analyze-button" className="shrink-0">
                  {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Fetch &amp; analyze
                </Button>
              </div>
              <p className="mt-1.5 text-[11px] text-slate-400">Only the portal&apos;s <code>/ns-api/</code> trace export link is accepted (object=trace&amp;action=export). Your API key is never stored.</p>
            </div>
          </Card>

          {/* Iter 118 — cross-trace queue rollup (7/30/90 days). */}
          <QueueRollup onOpenAnalysis={openHistory} />

          {/* Processing indicator */}
          {analyzing && (
            <Card className="p-6 flex flex-col items-center justify-center gap-3 text-center" data-testid="sip-processing">
              <Loader2 className="h-7 w-7 animate-spin text-primary" />
              <div>
                <p className="text-sm font-medium text-slate-700">Analyzing your call trace(s)…</p>
                <p className="text-xs text-slate-400">This can take up to a minute for large files. You can keep working — it&apos;ll appear under &ldquo;Recent analyses&rdquo; when done.</p>
              </div>
            </Card>
          )}

          {/* Results */}
          {result && !analyzing && (            <Card className="p-5" data-testid="sip-result">
              <div className="mb-4 flex items-center justify-between">
                <div><h2 className="text-lg font-semibold text-slate-800">{result.title}</h2><p className="text-xs text-slate-400">Analyzed {fmtDateTime(result.created_at)} · by {result.created_by_name}</p></div>
              </div>
              {/* Iter 91k — guard against empty/legacy analyses where the
                  background task never completed. Without this guard the
                  Tabs render as an empty strip with just a PDF button. */}
              {resultFiles.length === 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-start gap-2"
                     data-testid="sip-empty-result">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold">This analysis has no completed files.</div>
                    <div className="mt-1 text-xs">
                      It likely stalled during processing. Delete it from
                      &ldquo;Recent analyses&rdquo; and re-run the trace to get a
                      fresh timeline, summary and diagnosis.
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {result.queue_insight?.queues?.length > 0 && (
                    <div className="mb-5"><QueueInsight insight={result.queue_insight} /></div>
                  )}
                  {showComparison && (
                    <div className="mb-5 space-y-1.5">
                      <h3 className="text-sm font-semibold text-slate-800">Comparison</h3>
                      <DataTable headers={["File", "Caller", "Callee", "Duration", "Disposition", "Answered by"]}
                        rows={result.comparison.map((c) => [c.file, c.caller, c.callee, c.duration, c.disposition, c.answered_by])} testid="sip-comparison" />
                    </div>
                  )}
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <div className="flex items-center justify-between gap-2">
                      <TabsList className="flex-wrap">
                        {resultFiles.map((f, i) => <TabsTrigger key={i} value={String(i)} data-testid={`sip-tab-${i}`}>{f.name.length > 24 ? f.name.slice(0, 22) + "…" : f.name}</TabsTrigger>)}
                      </TabsList>
                      <div className="flex items-center gap-2">
                        {resultFiles.length > 1 && <Button variant="outline" size="sm" onClick={downloadZip} disabled={pdfBusy} data-testid="sip-download-zip"><Download className="h-4 w-4" /> Download all (ZIP)</Button>}
                        <Button variant="outline" size="sm" onClick={() => downloadPdf(Number(activeTab))} disabled={pdfBusy} data-testid="sip-download-pdf"><Download className="h-4 w-4" /> PDF</Button>
                      </div>
                    </div>
                    {resultFiles.map((f, i) => <TabsContent key={i} value={String(i)} className="mt-4"><FileAnalysis file={f} /></TabsContent>)}
                  </Tabs>
                </>
              )}
            </Card>
          )}
        </div>

        {/* History */}
        <Card className="h-fit p-4" data-testid="sip-history">
          <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><History className="h-4 w-4 text-slate-400" /> Recent analyses</h3>
          {isSuper && history.length > 0 && (
            <div className="mb-3 space-y-1.5" data-testid="sip-history-filters">
              <Select value={authorFilter} onValueChange={setAuthorFilter}>
                <SelectTrigger className="h-8 text-xs" data-testid="sip-filter-user"><SelectValue placeholder="All users" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All users</SelectItem>
                  {authorOptions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={orgFilter} onValueChange={setOrgFilter}>
                <SelectTrigger className="h-8 text-xs" data-testid="sip-filter-org"><SelectValue placeholder="All partners" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All partners / orgs</SelectItem>
                  {orgOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
              {(authorFilter !== "all" || orgFilter !== "all") && (
                <button onClick={() => { setAuthorFilter("all"); setOrgFilter("all"); }} data-testid="sip-filter-clear"
                  className="text-[11px] font-medium text-primary hover:underline">
                  Clear filters ({visibleHistory.length} of {history.length})
                </button>
              )}
            </div>
          )}
          {visibleHistory.length === 0 ? (
            <p className="py-6 text-center text-xs text-slate-400" data-testid="sip-history-empty">
              {history.length === 0 ? "No analyses yet." : "No analyses match these filters."}
            </p>
          ) : (
            <div className="space-y-1.5">
              {visibleHistory.map((h) => {
                const isProcessing = h.status === "processing";
                const isError      = h.status === "error";
                return (
                <div key={h.analysis_id} onClick={() => openHistory(h.analysis_id)} data-testid={`sip-history-item-${h.analysis_id}`}
                  className={`group flex cursor-pointer items-center justify-between rounded-lg border px-3 py-2 text-sm hover:bg-slate-50 ${result?.analysis_id === h.analysis_id ? "border-primary/40 bg-primary/5" : "border-slate-100"}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate font-medium text-slate-700">{h.title}</span>
                      {isProcessing && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-blue-700"
                              data-testid={`sip-history-processing-${h.analysis_id}`}>
                          <Loader2 className="h-2.5 w-2.5 animate-spin" /> Processing
                        </span>
                      )}
                      {isError && (
                        <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-rose-700"
                              data-testid={`sip-history-error-${h.analysis_id}`}>
                          Failed
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400">{fmtDateTime(h.created_at)} · {(h.comparison || []).length} call(s)</div>
                    {isSuper && (
                      <div className="text-[11px] text-slate-400" data-testid={`sip-history-author-${h.analysis_id}`}>
                        {h.created_by_name || "Unknown user"}{h.owner_org_name ? ` · ${h.owner_org_name}` : ""}
                      </div>
                    )}
                  </div>
                  <button onClick={(e) => deleteHistory(h.analysis_id, e)} data-testid={`sip-history-delete-${h.analysis_id}`} className="ml-2 shrink-0 text-slate-300 opacity-0 group-hover:opacity-100 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
                </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
